#!/usr/bin/env node

import { Git } from "./git.js";
import { loadConfig, type ConfigType } from "./config.js";
import type { ConfigTemplateType } from "./config.js";
import { AppError } from "./app-error.js";
import {
  askForBody,
  askForConfirmation,
  askForDescription,
  askForIssue,
  askForScope,
  askForType,
} from "./questions.js";
import type { AnswersType } from "./questions.js";
import { splitText } from "./utils.js";

async function main(): Promise<void> {
  try {
    const git = new Git();

    if (!(await git.isCurrentDirUnderGitControl())) {
      throw new AppError("Current directory is not a git repository.");
    }

    if (!(await git.haveStagedChanges())) {
      throw new AppError("Nothing to commit");
    }

    const config = loadConfig();
    const remoteOriginUrl = (await git.getGitRemoteOriginUrl()).trim();
    const template = getMatchingTemplate(config, remoteOriginUrl);
    console.info(`Using template "${template.name}"\n`);

    const answers: AnswersType = {
      type: (await askForType()).trim(),
      scope: (await askForScope()).trim(),
      description: (await askForDescription()).trim(),
      body: splitText((await askForBody()).trim(), 100),
      issue: (await askForIssue()).trim(),
    };

    const msg = await createMsg(answers, template);
    console.info(`${"-".repeat(42)}\n${msg}\n${"-".repeat(42)}`);
    if (await askForConfirmation()) {
      await git.commit(msg);
      console.info("done");
    }
  } catch (err) {
    if (err instanceof AppError) {
      console.error(err.message);
      process.exit(100);
    } else if (
      err instanceof Error &&
      err.message.startsWith("User force closed the prompt with")
    ) {
      // Abort exception of @inquirer
      process.exit(0);
    } else {
      console.error(err);
      process.exit(1);
    }
  }
}

function getMatchingTemplate(config: ConfigType, remoteOriginUrl: string): ConfigTemplateType {
  // ToDo: rename var
  let template2: ConfigTemplateType | undefined;

  for (const template of config.templates) {
    const regEx = new RegExp(template.regex);
    if (regEx.test(remoteOriginUrl)) {
      template2 = template;
    }
  }

  if (!template2) {
    throw new AppError(`No matching template found for "${remoteOriginUrl}"`);
  }

  return template2;
}

async function createMsg(answers: AnswersType, template: ConfigTemplateType): Promise<string> {
  const body = answers.body.join("\n");

  let msg = template.pattern;

  if (answers.type) {
    const typeText = `${template.pre_type ?? ""}${answers.type}${template.post_type ?? ""}`;
    msg = msg.replace("{type}", typeText);
  } else {
    msg = msg.replace("{type}", "");
  }

  if (answers.scope) {
    const scopeText = `${template.pre_scope ?? ""}${answers.scope}${template.post_scope ?? ""}`;
    msg = msg.replace("{scope}", scopeText);
  } else {
    msg = msg.replace("{scope}", "");
  }

  if (answers.description) {
    const descriptionText = `${template.pre_description ?? ""}${answers.description}${
      template.post_description ?? ""
    }`;
    msg = msg.replace("{description}", descriptionText);
  } else {
    msg = msg.replace("{description}", "");
  }

  if (body) {
    const bodyText = `${template.pre_body ?? ""}${body}${template.post_body ?? ""}`;
    msg = msg.replace("{body}", bodyText);
  } else {
    msg = msg.replace("{body}", "");
  }

  if (answers.issue) {
    const issueText = `${template.pre_issue ?? ""}${answers.issue}${template.post_issue ?? ""}`;
    msg = msg.replace("{issue}", issueText);
    const issue2Text = `${template.pre_issue2 ?? ""}${answers.issue}${template.post_issue2 ?? ""}`;
    msg = msg.replace("{issue2}", issue2Text);
    const issue3Text = `${template.pre_issue3 ?? ""}${answers.issue}${template.post_issue3 ?? ""}`;
    msg = msg.replace("{issue3}", issue3Text);
  } else {
    msg = msg.replace("{issue}", "");
    msg = msg.replace("{issue2}", "");
    msg = msg.replace("{issue3}", "");
  }

  return msg;
}

main().catch(console.error);
